import React, { useRef } from 'react';
import {
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  Plus,
  Save as SaveIcon,
  Search,
  Settings,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import { t } from '../../i18n/t';
import { LopButton } from '../LopButton';
import { useAsyncAction } from '../../hooks/useAsyncAction';
import '../LopCrudPage/LopCrudPage.css';

// The standard grid toolbar + status bar, shared by LopCrudPage and the
// bespoke grids (Holdings, FundDataHistory) so every grid looks and behaves
// the same. Markup/classes are the original LopCrudPage ones.

export interface LopGridToolbarProps {
  quickFilter: string;
  onQuickFilterChange: (v: string) => void;
  /** Read-only grids: Add/Delete/Save/Undo are still shown, but disabled. */
  readOnly?: boolean;
  showAdd?: boolean;
  showDelete?: boolean;
  addDisabled?: boolean;
  deleteDisabled?: boolean;
  saveDisabled?: boolean;
  cancelDisabled?: boolean;
  exportDisabled?: boolean;
  onAdd?: () => unknown;
  onDelete?: () => unknown;
  /** May return a promise - the button then shows a spinner until it settles. */
  onSave?: () => unknown;
  onCancel?: () => unknown;
  onExport: () => unknown;
}

export function LopGridToolbar(props: LopGridToolbarProps) {
  const {
    quickFilter,
    onQuickFilterChange,
    readOnly = false,
    showAdd = true,
    showDelete = true,
    addDisabled = false,
    deleteDisabled = false,
    saveDisabled = false,
    cancelDisabled = false,
    exportDisabled = false,
    onAdd,
    onDelete,
    onSave,
    onCancel,
    onExport,
  } = props;
  const searchRef = useRef<HTMLInputElement>(null);
  // Quick clear: the x appears once there is text; Escape in the box does the same.
  const clearSearch = () => {
    onQuickFilterChange('');
    searchRef.current?.focus();
  };
  return (
    <div className="lop-crud-page__toolbar">
      <div className="lop-crud-page__search">
        <Search size={16} className="lop-crud-page__search-icon" aria-hidden="true" />
        <input
          ref={searchRef}
          type="text"
          value={quickFilter}
          onChange={(e) => onQuickFilterChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && quickFilter) {
              e.preventDefault();
              e.stopPropagation();
              clearSearch();
            }
          }}
          placeholder={t('crud.searchPlaceholder')}
          className={`lop-crud-page__search-input${quickFilter ? ' lop-crud-page__search-input--has-value' : ''}`}
        />
        {quickFilter && (
          <button
            type="button"
            className="lop-crud-page__search-clear"
            onClick={clearSearch}
            title={t('crud.clearSearch')}
            aria-label={t('crud.clearSearch')}
          >
            <X size={14} aria-hidden="true" />
          </button>
        )}
      </div>
      <div className="lop-crud-page__actions">
        {showAdd && (
          <LopButton
            iconOnly
            icon={<Plus size={18} />}
            onClick={onAdd}
            disabled={readOnly || addDisabled}
            title={t('crud.tooltipAdd')}
            aria-label={t('crud.tooltipAdd')}
          />
        )}
        {showDelete && (
          <LopButton
            iconOnly
            icon={<Trash2 size={18} />}
            onClick={onDelete}
            disabled={readOnly || deleteDisabled}
            title={t('crud.tooltipDelete')}
            aria-label={t('crud.tooltipDelete')}
          />
        )}
        <LopButton
            iconOnly
            icon={<SaveIcon size={18} />}
            onClick={onSave}
            disabled={readOnly || saveDisabled}
            title={t('crud.tooltipSave')}
            aria-label={t('crud.tooltipSave')}
          />
        <LopButton
            iconOnly
            icon={<Undo2 size={18} />}
            onClick={onCancel}
            disabled={readOnly || cancelDisabled}
            title={t('crud.tooltipCancel')}
            aria-label={t('crud.tooltipCancel')}
          />
        <LopButton
            iconOnly
            icon={<FileSpreadsheet size={18} />}
            onClick={onExport}
            disabled={exportDisabled}
            title={t('crud.tooltipExport')}
            aria-label={t('crud.tooltipExport')}
          />
      </div>
    </div>
  );
}

export interface LopGridStatusBarProps {
  totalCount: number;
  displayedCount: number;
  /** Show the filtered "X of Y" text instead of the plain row count. */
  isFiltered: boolean;
  /** Omit on grids without row selection. */
  selectedCount?: number;
  customizationDirty: boolean;
  /** May return a promise - the button then shows a spinner until it settles. */
  onSaveCustomization: () => unknown;
  customizationToastVisible: boolean;
  /** Right side (pending data changes: +added ✎modified −deleted). */
  children?: React.ReactNode;
}

export function LopGridStatusBar(props: LopGridStatusBarProps) {
  const {
    totalCount,
    displayedCount,
    isFiltered,
    selectedCount,
    customizationDirty,
    onSaveCustomization,
    customizationToastVisible,
    children,
  } = props;
  const { run: runSaveCustomization, running: savingCustomization } = useAsyncAction(
    async () => {
      await onSaveCustomization();
    },
  );
  return (
    <>
      <div className="lop-crud-page__statusbar">
        <div className="lop-crud-page__statusbar-info">
          <span>
            {isFiltered
              ? t('crud.statusBar.filtered', { displayed: displayedCount, total: totalCount })
              : t('crud.statusBar.rows', { count: totalCount })}
          </span>
          {selectedCount !== undefined && (
            <>
              <span className="lop-crud-page__statusbar-separator">·</span>
              <span>{t('crud.statusBar.selected', { count: selectedCount })}</span>
            </>
          )}
          {/* Save customization (layout + formatting). Floppy WITH a gear =
              "save the grid settings", distinct from the data-save button.
              Lit only while there is an unsaved customization change. */}
          <button
            type="button"
            className={
              'lop-crud-page__statusbar-save-cust' +
              (customizationDirty ? ' lop-crud-page__statusbar-save-cust--dirty' : '')
            }
            onClick={() => runSaveCustomization()}
            disabled={!customizationDirty || savingCustomization}
            aria-busy={savingCustomization ? true : undefined}
            title={t('crud.statusBar.saveCustomization')}
            aria-label={t('crud.statusBar.saveCustomization')}
            data-testid="save-customization"
          >
            {savingCustomization ? (
              <Loader2 size={15} className="lop-btn__spinner" aria-hidden="true" />
            ) : (
              <>
                <SaveIcon size={15} aria-hidden="true" />
                <Settings
                  size={9}
                  className="lop-crud-page__statusbar-save-cust-gear"
                  aria-hidden="true"
                />
              </>
            )}
          </button>
        </div>
        {children}
      </div>
      {customizationToastVisible && (
        <div className="lop-crud-page__toast" role="status" aria-live="polite">
          <CheckCircle2 size={18} aria-hidden="true" />
          <span>{t('crud.toast.customizationSaved')}</span>
        </div>
      )}
    </>
  );
}
